import React from 'react'
import { useDispatch } from 'react-redux'
import { FieldArray } from 'formik'
import { useTranslation } from 'react-i18next'
import { deleteProductGroup, setProductGroupTranslation, updateProductGroup } from 'app-support/productgroup/state/actions'
import * as Yup from 'yup'
import SubgroupName, { IDropdownOption } from './Edit/SubgroupName'
import ArticlesCodes from './Edit/ArticlesCodes'
import { useHistory, useParams } from 'react-router-dom'
import { useNotifier } from '@cbgms/base/notifier'
import { useGetProductTree } from '../hooks/useGetProductTree'
import { useQueryClient } from '@cbgms/base/http/use-query'
import { productTreeCacheKey } from '../hooks/cache-keys'
import { Locales } from '@carsys/enums/locales'
import { BasicFormLayout, Form } from '@carsys/form'
import { DeleteButton, nonRejectionResult } from '@cbgms/components/delete-flow'
import { Grid } from '@mui/material'

interface IFormValues {
  LinkedMainGroup: string
  Name: string
  Locale: Locales
  IsActive: boolean
  HbaseArticleCodes: number[]
  GenericArticles: number[]
  Components: string[]
}

export function Edit() {
  const { t } = useTranslation()
  const notifier = useNotifier()
  const history = useHistory()
  const queryClient = useQueryClient()
  const dispatch = useDispatch()

  const { tree_uuid } = useParams<{ tree_uuid: string }>()
  const { maingroup_uuid } = useParams<{ maingroup_uuid: string }>()
  const { subgroup_uuid } = useParams<{ subgroup_uuid: string }>()
  const { data: ProductGroup } = useGetProductTree(tree_uuid)

  let initialValues: IFormValues = {
    Locale: Locales.nl_NL,
    LinkedMainGroup: '',
    Name: '',
    IsActive: false,
    GenericArticles: [],
    HbaseArticleCodes: [],
    Components: []
  }
  let options: IDropdownOption[] = []

  if (ProductGroup?.Categories) {
    const mainGroup = ProductGroup.Categories.find(a => a.UUID === maingroup_uuid)
    const subGroup = mainGroup?.Categories?.find(a => a.UUID === subgroup_uuid)

    options =
      ProductGroup &&
      ProductGroup.Categories.map(category => ({
        key: category.UUID,
        text: category.Name,
        value: category.UUID
      }))

    initialValues = {
      Locale: ProductGroup.Locale,
      LinkedMainGroup: mainGroup?.UUID || '',
      Name: subGroup?.Name || '',
      IsActive: subGroup?.IsActive || false,
      GenericArticles: subGroup?.GenericArticles || [],
      HbaseArticleCodes: subGroup?.HbaseArticleCodes || [],
      Components: subGroup?.Components || []
    }
  }

  const handleSubmit = async (values: IFormValues) => {
    const productGroup = {
      ParentUUID: values.LinkedMainGroup,
      GenericArticles: values.GenericArticles,
      HbaseArticleCodes: values.HbaseArticleCodes,
      Components: values.Components,
      IsActive: values.IsActive
    }

    const translation = {
      Locale: values.Locale,
      Name: values.Name
    }

    try {
      await Promise.all([
        dispatch(updateProductGroup(subgroup_uuid, productGroup)),
        dispatch(setProductGroupTranslation(subgroup_uuid, translation))
      ])

      queryClient.invalidateQueries(productTreeCacheKey(tree_uuid))

      return values
    } catch (e: any) {
      throw new Error(e)
    }
  }

  const onSubmitSuccess = (values: IFormValues) => {
    notifier.success(t('global.successfully_saved'))
    history.push(`/product-group/${tree_uuid}/main-group/${values.LinkedMainGroup}/sub-group/${subgroup_uuid}`)
  }

  const onDelete = async (dryRun: boolean) => {
    try {
      if (dryRun) {
        return nonRejectionResult
      } else {
        return await dispatch(deleteProductGroup(subgroup_uuid))
      }
    } catch (e: any) {
      notifier.error(t('global.http_unknown_error'))
      throw new Error(e)
    }
  }

  const onDeleteSuccess = async () => {
    notifier.success(t('global.successfully_deleted'))

    history.push(`/product-group/${tree_uuid}/main-group/${maingroup_uuid}`)
  }

  const validationSchema = Yup.object().shape({
    Name: Yup.string().required(),
    IsActive: Yup.boolean().optional(),
    GenericArticles: Yup.array().of(Yup.number().required()),
    HbaseArticleCodes: Yup.array().of(Yup.number().required()),
    Components: Yup.array().of(Yup.string().required())
  })

  return (
    <Form
      confirmationRequired
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      onSubmitSuccess={onSubmitSuccess}
      validationSchema={validationSchema}
    >
      {({ values }) => (
        <BasicFormLayout
          deleteButton={
            onDelete &&
            onDeleteSuccess && (
              <Grid item>
                <DeleteButton
                  handleDelete={onDelete}
                  handleDeleteSuccess={onDeleteSuccess}
                  deletedItemString={initialValues.Name!}
                  scope='subgroup'
                />
              </Grid>
            )
          }
          backButtonProps={{ to: () => `/product-group/${tree_uuid}/main-group/${maingroup_uuid}` }}
        >
          <Grid container spacing={2}>
            <SubgroupName options={options} />

            <Grid item xs={4}>
              <FieldArray name='GenericArticles'>
                {({ remove, push }) => (
                  <ArticlesCodes
                    type='number'
                    title='Generic'
                    codesType='GenericArticles'
                    codes={values.GenericArticles}
                    remove={remove}
                    push={push}
                  />
                )}
              </FieldArray>
            </Grid>

            <Grid item xs={4}>
              <FieldArray name='HbaseArticleCodes'>
                {({ remove, push }) => (
                  <ArticlesCodes
                    type='number'
                    title='HBase'
                    codesType='HbaseArticleCodes'
                    codes={values.HbaseArticleCodes}
                    remove={remove}
                    push={push}
                  />
                )}
              </FieldArray>
            </Grid>
            <Grid item xs={4}>
              <FieldArray name='Components'>
                {({ remove, push }) => (
                  <ArticlesCodes type='text' title='MDM' codesType='Components' codes={values.Components} remove={remove} push={push} />
                )}
              </FieldArray>
            </Grid>
          </Grid>
        </BasicFormLayout>
      )}
    </Form>
  )
}

export default React.memo(Edit)
